import { getTenantFullName } from '~/components/tenants';
import type { BreadcrumbGenerator, BreadcrumbItem, BreadcrumbContext } from './types';
export const item = (label: string, link?: string, linkClass?: string): BreadcrumbItem => ({
  label,
  link,
  linkClass,
});

export const dynamicItem =
  (
    labelFn: (context: BreadcrumbContext) => string,
    linkFn?: (context: BreadcrumbContext) => string,
    linkClassFn?: (context: BreadcrumbContext) => string
  ): BreadcrumbGenerator =>
  (context) => [item(labelFn(context), linkFn?.(context), linkClassFn?.(context))];

export const staticItem =
  (label: string, link?: string, linkClass?: string): BreadcrumbGenerator =>
  (context) => [item(context.t(label), link, linkClass)];
export const combine =
  (...generators: BreadcrumbGenerator[]): BreadcrumbGenerator =>
  (context) =>
    generators.flatMap((gen) => gen(context));

export const chain =
  (base: BreadcrumbGenerator, ...additional: BreadcrumbGenerator[]): BreadcrumbGenerator =>
  (context) => [...base(context), ...additional.flatMap((gen) => gen(context))];
export const when =
  (
    condition: (context: BreadcrumbContext) => boolean,
    generator: BreadcrumbGenerator,
    fallback?: BreadcrumbGenerator
  ): BreadcrumbGenerator =>
  (context) =>
    condition(context) ? generator(context) : fallback?.(context) || [];

export const unless = (condition: (context: BreadcrumbContext) => boolean, generator: BreadcrumbGenerator): BreadcrumbGenerator =>
  when((ctx) => !condition(ctx), generator);
export const portfolioItem = (): BreadcrumbGenerator => (context) => {
  const property = context.currentProperty();
  const portfolioName = property?.portfolio?.name;
  const portfolioId = property?.portfolio?.id;
  return [item(portfolioName || context.t('Loading'), context.isLoaded ? `/portfolios/${portfolioId}` : context.path)];
};

export const propertyItem = (): BreadcrumbGenerator => (context) => {
  const property = context.currentProperty();
  const propertyName = property?.name;
  const portfolioId = property?.portfolio?.id;
  const propertyId = property?.id;
  return [
    item(propertyName || context.t('Loading'), context.isLoaded ? `/portfolios/${portfolioId}/properties/${propertyId}` : context.path),
  ];
};

export const unitItem = (): BreadcrumbGenerator => (context) => {
  const unitName = context.unit()?.name;
  return [item(unitName || context.t('Loading'))];
};

export const tenantItem = (): BreadcrumbGenerator => (context) => {
  const tenantId = context.path.match(/\d+/g)?.[0];
  return [item(getTenantFullName(context.currentTenant()) || context.t('Loading'), `/users/tenants/${tenantId}`)];
};

export const vendorItem = (): BreadcrumbGenerator => (context) => {
  const vendor = context.currentVendor();
  return [item(vendor?.name || context.t('Loading'), `/maintenance/vendors/${vendor?.id}`)];
};

export const reportItem =
  (reportKey: string, labelKey: string): BreadcrumbGenerator =>
  (context) => [item(context.t(labelKey), `/reports/${reportKey}`, 'text-black')];
export const crudBreadcrumb =
  (section: string, sectionPath: string, label: string, basePath: string, action: string) => (context: BreadcrumbContext) => [
    { label: context.t(section) },
    { label: context.t(label), link: basePath },
    { label: context.t(action) },
  ];

export const detailBreadcrumb =
  (section: string, sectionPath: string, label: string, basePath: string, detailLabel: string) => (context: BreadcrumbContext) => [
    { label: context.t(section) },
    { label: context.t(label), link: basePath },
    { label: context.t(detailLabel) },
  ];

export const listBreadcrumb = (section: string, sectionPath: string, label: string, basePath: string) => (context: BreadcrumbContext) => [
  { label: context.t(section) },
  { label: context.t(label), link: basePath },
];

export const propertiesBase: BreadcrumbGenerator = (context) => {
  const link = context.path === '/portfolios' ? context.path : '/portfolios';
  return [item(context.t('Properties'), link)];
};

export const propertyChain = combine(propertiesBase, portfolioItem(), propertyItem());
export const unitChain = combine(propertiesBase, portfolioItem(), propertyItem(), unitItem());

export const safeCurrentVendor = (context: BreadcrumbContext) => context.currentVendor?.() || { name: context.t('Loading'), id: '' };

export const safeUnit = (context: BreadcrumbContext) => context.unit?.() || { name: context.t('Loading'), id: '' };

export const safeApplication = (context: BreadcrumbContext) => context.applicationStore?.application || null;

export const safeCurrentTenant = (context: BreadcrumbContext) => context.currentTenant?.() || null;

export const safePropertyBreadcrumb = (): BreadcrumbGenerator => (context) => {
  const property = context.currentProperty?.();
  const isLoaded = context.isLoaded;

  if (!property || !isLoaded) {
    return [item(context.t('Loading')), item(context.t('Loading'))];
  }

  const portfolioId = property.portfolio?.id;
  const propertyId = property.id;
  const portfolioName = property.portfolio?.name;
  const propertyName = property.name;

  return [
    item(portfolioName || context.t('Loading'), `/portfolios/${portfolioId}`),
    item(propertyName || context.t('Loading'), `/portfolios/${portfolioId}/properties/${propertyId}`),
  ];
};

export const nestedCrudBreadcrumb =
  (
    section: string,
    sectionPath: string,
    parent: string,
    parentPath: string,
    entity: string,
    entityPath: string,
    action: string,
    actionPath?: string
  ): BreadcrumbGenerator =>
  (context) => [
    item(context.t(section)),
    item(context.t(parent), parentPath),
    item(context.t(entity), entityPath),
    item(context.t(action), actionPath),
  ];

export const nestedDetailBreadcrumb =
  (
    section: string,
    sectionPath: string,
    parent: string,
    parentPath: string,
    entity: string,
    entityPath: string,
    detailLabel: string
  ): BreadcrumbGenerator =>
  (context) => [
    item(context.t(section)),
    item(context.t(parent), parentPath),
    item(context.t(entity), entityPath),
    item(context.t(detailLabel)),
  ];

export const nestedListBreadcrumb =
  (section: string, sectionPath: string, parent: string, parentPath: string, entity: string, entityPath: string): BreadcrumbGenerator =>
  (context) => [item(context.t(section)), item(context.t(parent), parentPath), item(context.t(entity), entityPath)];
